import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MaintenanceDetails } from '../../../interface/maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ConfirmationWarning from '../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import ConvertToUTC from '../../../shared-functions/ConvertToUTC';
import maintenanceSchedule from '../../../utils/api/maintenance-schedule';

interface MaintenanceContentProperties {
  maintenanceDetails: MaintenanceDetails;
  onChangeValue: (name: string, value: string | number) => void;
  openSuccessModal: () => void;
  closeModal: () => void;
}

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Show warning when navigate back
 */
function MaintenanceContent({
  maintenanceDetails,
  onChangeValue,
  closeModal,
  openSuccessModal,
}: MaintenanceContentProperties) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { allStations } = useAppSelector((state) => state.approvedStation);
  const { allDevices } = useAppSelector((state) => state.device);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });

  const [formStatus, setFormStatus] = useState({
    buttonLoader: false,
  });

  /**
   * Handle form submission and open modal with MaintenanceContent
   */
  const handleNavigateBack = () => {
    closeModal(); // Close the modal when navigating back
    navigate('../schedule');
  };

  /**
   * Cancel schedule and create maintenance schedule
   */
  const handleCancelExistingSchedules = () => {
    const request = {
      chargingStation: maintenanceDetails.station,
      chargingPoint: maintenanceDetails.device,
      startDateUtc: ConvertToUTC(maintenanceDetails.time, maintenanceDetails.fromDate),
      type: maintenanceDetails.maintenance,
      priority: maintenanceDetails.priority,
    };
    maintenanceSchedule
      .cancelScheduleAndAddMaintenanceSchedule(request)
      .then(() => {
        openSuccessModal();
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Form submit function
   */
  const submitForm = () => {
    setFormStatus({
      buttonLoader: true,
    });
    const request = {
      chargingStation: maintenanceDetails.station,
      chargingPoint: maintenanceDetails.device,
      startDateUtc: ConvertToUTC(maintenanceDetails.time, maintenanceDetails.fromDate),
      type: maintenanceDetails.maintenance,
      priority: maintenanceDetails.priority,
    };
    maintenanceSchedule
      .addMaintenanceSchedule(request)
      .then(() => {
        setFormStatus({
          buttonLoader: false,
        });
        openSuccessModal();
      })
      .catch((error) => {
        setFormStatus({
          buttonLoader: false,
        });
        if (error?.response?.data?.message === 'A confirmed Scheduled Charging Session detected') {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            header: false,
            content: (
              <ConfirmationWarning
                selectedId=""
                onCancel={handleNavigateBack}
                message={
                  <>
                    ⚠️ Scheduled Charging Session Detected ⚠️ <br /> The charging device you
                    selected for maintenance currently has a scheduled charging session by an end
                    user. Would you like to proceed with the maintenance, or would you prefer to
                    reschedule it?
                  </>
                }
                cancelLabel="Reschedule"
                confirmLabel="Proceed"
                onConfirm={handleCancelExistingSchedules}
              />
            ),
            notificationModal: true,
          });
        } else {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        }
      });
  };

  return (
    <div className="content-maintenance-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="content-maintenance-header">
        <h3>Maintenance Schedule</h3>
      </div>
      <div className="content-maintenance-container">
        <div className="content-maintenance-from-wrap">
          <div className="content-maintenance-from">
            <CustomInput
              inputLabel="Station Name"
              type="text"
              placeholder=""
              name="name"
              value={
                allStations.find((station) => station.value === maintenanceDetails.station)
                  ?.label || ''
              }
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              inputLabel="Device"
              type="text"
              placeholder=""
              name="device"
              value={
                allDevices.find((station) => station.value === maintenanceDetails.device)?.label ||
                ''
              }
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              name="fromDate"
              inputLabel="Date"
              placeholder=""
              type="text"
              value={maintenanceDetails.fromDate}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              name="time"
              inputLabel="Time"
              placeholder=""
              type="text"
              value={maintenanceDetails.time}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              inputLabel="Type of Maintenance"
              type="text"
              placeholder=""
              name="maintenance"
              value={maintenanceDetails.maintenance}
              onChangeValue={onChangeValue}
              readOnly
            />
            <CustomInput
              inputLabel="Priority"
              type="text"
              placeholder=""
              name="priority"
              value={maintenanceDetails.priority}
              onChangeValue={onChangeValue}
              readOnly
            />
          </div>
        </div>
        <div className="content-maintenance-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label="Cancel" buttonType="back" onClickFunc={handleNavigateBack} />
            <MyButton
              label="Confirm"
              buttonType="submit"
              onClickFunc={submitForm}
              disableHoverEffect
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceContent;
